import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import { useAudio } from './AudioProvider'
import Flex from 'components/Flex'
import Icon from 'components/Icon'
import PlayerLayout from './PlayerLayout'
import { ACTIVE_LEAD_TAB_STORAGE_KEY, LEAD_TAB_ID, Z_INDEX } from 'utils/constants'
import useSessionStorage from 'hooks/use-session-storage'
import { LeadDetailTab } from 'store/types/lead'

const FloatingAudioPlayer: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [animate, setAnimate] = useState(false)
  const {
    isPlaying,
    currentTime,
    duration,
    buffered,
    title,
    fallbackMessage,
    isInlineVisible,
    isClosed,
    errorCode,
    virtualCardIndex,
    handleBringBackCard,
    close,
    toggle,
    seek,
    changeVolume,
    changeSpeed,
  } = useAudio()
  const [_currentTab] = useSessionStorage<LeadDetailTab>(ACTIVE_LEAD_TAB_STORAGE_KEY, LEAD_TAB_ID.activity)

  useEffect(() => {
    if (isInlineVisible) return
    if (!ref.current) return

    const track = ref.current
    const firstText = track.children[0] as HTMLElement

    requestAnimationFrame(() => {
      const textWidth = firstText.scrollWidth
      const containerWidth = track.parentElement!.clientWidth

      setAnimate(textWidth > containerWidth)
    })
  }, [title, isInlineVisible])

  if (!duration) return null

  if (_currentTab == LEAD_TAB_ID.activity && isInlineVisible) {
    return null
  }

  if (isClosed) {
    return null
  }

  const backToCard = () => {
    handleBringBackCard && handleBringBackCard(virtualCardIndex as number)
  }

  return createPortal(
    <Container>
      <Flex gap="4px" justifyContent="space-between" flex={1} minWidth={0}>
        <MarqueeContainer>
          <Marquee animate={animate} onClick={backToCard}>
            <div className="track" ref={ref}>
              <span className="text">{title}</span>

              {animate && <span className="text">{title}</span>}
            </div>
          </Marquee>
        </MarqueeContainer>
        <Icon icon={['far', 'xmark']} color="white" onClick={close} />
      </Flex>

      <PlayerLayout
        errorCode={errorCode}
        fallbackMessage={fallbackMessage}
        isFloating={true}
        isPlaying={isPlaying}
        toggle={toggle}
        seek={seek}
        currentTime={currentTime}
        duration={duration}
        buffered={buffered}
        changeVolume={changeVolume}
        changeSpeed={changeSpeed}
      />
    </Container>,
    document.body,
  )
}

export default FloatingAudioPlayer

const Container = styled.div`
  position: fixed;
  bottom: 0;
  left: 52px;
  max-width: 400px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
  background: black;
  z-index: ${Z_INDEX.FloatingAudioPlayer};
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
`

const MarqueeContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); } 
`

const Marquee = styled.div<{ animate: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  width: 100%;

  .track {
    display: inline-flex;
    white-space: nowrap;
    will-change: transform;

    ${({ animate }) =>
      animate &&
      css`
        animation: ${scroll} 10s linear infinite;
      `}
  }

  .text {
    font-weight: 500;
    line-height: 16px;
    font-size: 12px;
    color: white;
    margin-right: 2rem;
  }
`
